import { useState, useMemo } from 'react'
import { MessageSquarePlus, Search, Trash2 } from 'lucide-react'
import type { ConversationListItem } from '../../lib/api'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

export interface ChatSidebarProps {
  conversations: ConversationListItem[]
  isLoading?: boolean
  selectedId: string | null | undefined
  onSelectConversation: (id: string) => void
  onNewConversation: () => void
  onDeleteConversation: (id: string) => void
  className?: string
}

export function ChatSidebar({
  conversations,
  isLoading = false,
  selectedId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  className = '',
}: ChatSidebarProps) {
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search.trim()) return conversations
    const q = search.trim().toLowerCase()
    return conversations.filter(
      (c) =>
        (c.title ?? '').toLowerCase().includes(q)
    )
  }, [conversations, search])

  return (
    <aside
      className={`w-72 flex flex-col border-r border-slate-200 bg-white ${className}`}
      aria-label="Lista de conversaciones"
    >
      <div className="p-3 border-b border-slate-200">
        <button
          type="button"
          onClick={onNewConversation}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          <MessageSquarePlus className="w-4 h-4" />
          Nueva conversación
        </button>
      </div>
      <div className="p-2 border-b border-slate-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar conversaciones..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            aria-label="Buscar conversaciones"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="p-4 flex justify-center">
            <span className="inline-block w-6 h-6 border-2 border-slate-300 border-t-emerald-600 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-sm text-slate-500">
            {search.trim()
              ? 'No hay conversaciones que coincidan.'
              : 'No hay conversaciones. Crea una nueva.'}
          </div>
        ) : (
          <ul className="py-2">
            {filtered.map((conv) => (
              <li key={conv.id}>
                <div
                  className={`group flex items-center gap-2 px-3 py-2.5 mx-2 rounded-lg cursor-pointer transition-colors ${
                    selectedId === conv.id
                      ? 'bg-emerald-50 text-emerald-800'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                  onClick={() => onSelectConversation(conv.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelectConversation(conv.id)
                    }
                  }}
                >
                  <span className="flex-1 min-w-0 truncate text-sm">
                    {conv.title || 'Sin título'}
                  </span>
                  <span className="flex-shrink-0 text-xs text-slate-400">
                    {formatDistanceToNow(new Date(conv.updated_at), {
                      addSuffix: true,
                      locale: es,
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteConversation(conv.id)
                    }}
                    className="flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-200 text-slate-500 hover:text-red-600 transition-opacity"
                    aria-label={`Eliminar conversación ${conv.title || conv.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}
